import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

/** Which token the limit-order price is denominated against. */
export type LimitPriceSide = 'sell' | 'buy';

/** A limit-order rate denominated for each side of the pair. */
export type LimitPriceRate = { sell: string; buy: string };

/**
 * The editable limit-order rate, tagged with the sell/buy pair key it was set
 * for. The form and chart share this value, while the key lets consumers ignore
 * a rate that belongs to a stale pair.
 */
export type TaggedLimitRate = { rate: LimitPriceRate; pairKey: string };

/**
 * A manual side selection tagged with the pair key it was made on. A pair change
 * invalidates the override and restores the default side for the new pair.
 */
export type LimitPriceSideOverride = { side: LimitPriceSide; pairKey: string };

type LimitRateStore = {
  taggedLimitRate: TaggedLimitRate;
  limitPriceSideOverride: LimitPriceSideOverride | null;
  setTaggedLimitRate: (rate: LimitPriceRate, pairKey: string) => void;
  setLimitPriceSideOverride: (override: LimitPriceSideOverride | null) => void;
  reset: () => void;
};

const INITIAL_TAGGED_LIMIT_RATE: TaggedLimitRate = {
  rate: { sell: '', buy: '' },
  pairKey: '',
};

export const useLimitRateStore = createWithEqualityFn<LimitRateStore>()(
  (set) => ({
    taggedLimitRate: INITIAL_TAGGED_LIMIT_RATE,
    limitPriceSideOverride: null,
    setTaggedLimitRate: (rate, pairKey) => set({ taggedLimitRate: { rate, pairKey } }),
    setLimitPriceSideOverride: (limitPriceSideOverride) => set({ limitPriceSideOverride }),
    reset: () => set({ taggedLimitRate: INITIAL_TAGGED_LIMIT_RATE, limitPriceSideOverride: null }),
  }),
  shallow,
);

export const taggedLimitRateSelector = (state: LimitRateStore) => state.taggedLimitRate;
export const executionLimitRateSelector = (state: LimitRateStore) => ({
  taggedLimitRate: state.taggedLimitRate,
  resetLimitRate: state.reset,
});
